import React, { useContext, useEffect } from "react";
import { Redirect, Link } from "react-router-dom";
import { LoginWrapper } from "./styled/SLogin";
import { Input, Button, Header, Divider } from "../../components";
import { Box } from "@rebass/grid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { AuthenticationContext } from "../../providers/AuthenticationProvider";
import { useTranslation } from "react-i18next";

interface FormValues {
  username: string;
  password: string;
}

const Login = () => {
  const {
    authenticate,
    authenticated,
    isLoading,
    authenticatePasswordless,
  } = useContext(AuthenticationContext);
  const { t } = useTranslation();

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .min(2, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 2 }))
      .max(50, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 50 }))
      .required(t("VALIDATION_REQUIRED")),
    password: Yup.string()
      .min(6, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 6 }))
      .max(50, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 50 }))
      .required(t("VALIDATION_REQUIRED")),
  });

  function getAutoLoginUsername() {
    const params = new URLSearchParams(window.location.search);
    return params.get("auto_login");
  }

  function userCanAutoLogin(username: string) {
    if (!process.env.REACT_APP_AUTO_LOGIN_USERS) return false;
    return process.env.REACT_APP_AUTO_LOGIN_USERS.split(",").includes(username);
  }

  useEffect(() => {
    if (
      process.env.REACT_APP_DEFAULT_AUTO_LOGIN &&
      process.env.REACT_APP_DEFAULT_AUTO_LOGIN !== ""
    ) {
      authenticatePasswordless(process.env.REACT_APP_DEFAULT_AUTO_LOGIN);
      return;
    }
    const username = getAutoLoginUsername();
    if (username !== null) {
      if (userCanAutoLogin(username)) {
        authenticatePasswordless(username, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authenticated) {
    return <Redirect to="documents" />;
  }

  return (
    <React.Fragment>
      <Formik
        validationSchema={LoginSchema}
        initialValues={{
          username: "",
          password: "",
        }}
        onSubmit={(values: FormValues) => {
          authenticate(values.username, values.password);
        }}
        render={({ touched, errors }) => (
          <Box id="box-login" data-page-id="login">
            <LoginWrapper>
              <Header>{t("WELCOME")}</Header>
              <Form method="post">
                <Box mt={30}>
                  <Input
                    id="input-username"
                    name="username"
                    type="text"
                    placeholder={t("USERNAME_OR_EMAIL")}
                    icon="user"
                    touched={touched.username}
                    error={errors.username}
                  />
                </Box>
                <Box mt={30}>
                  <Input
                    id="input-password"
                    name="password"
                    type="password"
                    placeholder={t("PASSWORD")}
                    icon="user_lock"
                    touched={touched.password}
                    error={errors.password}
                  />
                </Box>
                <Box my={30}>
                  <Button
                    id="button-login"
                    fullWidth={true}
                    kind="primary"
                    type="submit"
                    loading={isLoading}
                  >
                    {t("LOGIN")}
                  </Button>
                  <Box mt={10}>
                    <Link id={"link-password-reset"} to="/reset_password">
                      {t("FORGOT_PASSWORD")}
                    </Link>
                  </Box>
                </Box>
                <Divider />
                <Box mt={30}>
                  <Button
                    to={"/register"}
                    id="button-register"
                    fullWidth={true}
                    kind="secondary"
                    type="button"
                  >
                    {t("REGISTER")}
                  </Button>
                </Box>
              </Form>
            </LoginWrapper>
          </Box>
        )}
      />
    </React.Fragment>
  );
};

export default Login;
